import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { QueryBoundary, retryTransient } from "@/lib/queryState";
import { useLiveWindow } from "@/lib/marketHours";
import { formatSourceValue, hasRaw } from "@/lib/sourceFormat";
import { PageHead, Panel, DataBanner, DataStateBadge, sessionDateLabel, useViewport, etAndLocal, etStamp } from "@/sentinel";

const isStub = (src) => src.startsWith("ChainStub") || src.includes("STUB") || src.startsWith("fixture:");
const rawText = (v) => (v !== null && typeof v === "object" ? JSON.stringify(v, null, 2) : String(v));

/** LIVE only for today's session while the market is open; a recorded entry is otherwise FROZEN. */
const stateOf = (src, live) => (isStub(src) ? "STUB" : live ? "LIVE" : "FROZEN");

function RawToggle({ open, onToggle, label }) {
  return (
    <button type="button" className="sn-link sn-small" aria-expanded={open} onClick={onToggle} aria-label={`${open ? "Hide" : "Show"} raw value for ${label}`}>
      {open ? "hide raw" : "show raw"}
    </button>
  );
}

/**
 * One source-log entry in the ProvenanceRow grid (Source + vendor · State · Value · Timestamp ET · As-of).
 * The library's ProvenanceRow fixes its last column to "age / budget", which snapshot entries do not carry,
 * so this renders the same `sn-src` row locally.
 */
function SourceRow({ e, live }) {
  const [raw, setRaw] = useState(false);
  const label = e.input || e.field || "—";
  const ts = etStamp(e.timestamp_et || e.ts);
  const asof = e.as_of || e.ts || "—";
  const src = e.source || "";
  const state = stateOf(src, live);
  return (
    <div className="sn-src" role="row" data-state={state.toLowerCase()}>
      <span className="sn-src-name" role="cell" style={{ overflowWrap: "anywhere" }}>{label}<small>{src || "—"}</small></span>
      <span role="cell"><DataStateBadge state={state} /></span>
      <span role="cell" className="sn-small" style={{ color: "var(--text-primary)", overflowWrap: "anywhere", minWidth: 0 }}>
        <span className="sn-num" data-testid={`source-value-${label}`}>{formatSourceValue(label, e.value)}</span>
        {hasRaw(e.value) && <> <RawToggle open={raw} onToggle={() => setRaw((r) => !r)} label={label} /></>}
        {raw && <pre className="sn-num sn-small sn-faint" style={{ margin: "var(--space-1) 0 0", whiteSpace: "pre-wrap" }}>{rawText(e.value)}</pre>}
      </span>
      <span role="cell" className="sn-num sn-small" style={{ textAlign: "right", color: "var(--text-primary)" }}>{ts}</span>
      <span role="cell" className="sn-num sn-small" style={{ textAlign: "right", color: state === "STUB" ? "var(--provenance)" : "var(--text-secondary)" }}>{asof}</span>
    </div>
  );
}

function SourceHeader() {
  return (
    <div className="sn-src" role="row" style={{ minHeight: 32, background: "var(--table-header-bg)", color: "var(--text-secondary)", fontSize: 12, fontWeight: 500, borderBottom: "1px solid var(--border-strong)" }}>
      <span role="columnheader">Input · source</span>
      <span role="columnheader">State</span>
      <span role="columnheader">Value</span>
      <span role="columnheader" style={{ textAlign: "right" }}>Timestamp (ET · local)</span>
      <span role="columnheader" style={{ textAlign: "right" }}>As-of</span>
    </div>
  );
}

/** Mobile: each source becomes a card. */
function SourceCard({ e, live }) {
  const [raw, setRaw] = useState(false);
  const src = e.source || "";
  const state = stateOf(src, live);
  const label = e.input || e.field || "—";
  return (
    <div className="sn-rowcard" style={state === "STUB" ? { borderColor: "var(--provenance)" } : null}>
      <div className="sn-rowcard-h"><span style={{ fontWeight: 500, overflowWrap: "anywhere" }}>{label}</span><span className="sn-sp" /><DataStateBadge state={state} /></div>
      <span className="sn-small sn-faint" style={{ overflowWrap: "anywhere" }}>{src || "—"}</span>
      <span className="sn-small" style={{ overflowWrap: "anywhere" }}>
        <span className="sn-num">{formatSourceValue(label, e.value)}</span>
        {hasRaw(e.value) && <> <RawToggle open={raw} onToggle={() => setRaw((r) => !r)} label={label} /></>}
      </span>
      {raw && <pre className="sn-num sn-small sn-faint" style={{ margin: 0, whiteSpace: "pre-wrap" }}>{rawText(e.value)}</pre>}
      <span className="sn-small sn-muted">Timestamp <span className="sn-num">{e.timestamp_et || e.ts || "—"}</span> · as-of <span className="sn-num">{e.as_of || e.ts || "—"}</span></span>
    </div>
  );
}

export default function Sources() {
  const { selectedDate } = useAuth();
  const { mobile } = useViewport();
  const win = useLiveWindow(selectedDate);
  const query = useQuery({ queryKey: ["sources", selectedDate], queryFn: () => api.getSources(selectedDate), enabled: !!selectedDate, retry: retryTransient });

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="sources-page">
      <PageHead
        crumb={["Evidence", "Data provenance"]}
        title="Sources"
        context={<>{win.live ? "Live" : win.isToday ? "Saved" : "Archived"} · <span className="sn-sans-num">{sessionDateLabel(selectedDate)}</span> · every input, its source and when it was taken (ET)</>}
      />
      <QueryBoundary query={query} loading="page" what="the source log" errorTestId="sources-error"
        isEmpty={(d) => !(d?.snapshots || []).length}
        empty={{ icon: "Database", title: "No input snapshots yet", body: "Each run freezes a source log here.", testId: "sources-empty" }}>
        {(data) => <SourceLog snapshots={data.snapshots} mobile={mobile} live={win.live} />}
      </QueryBoundary>
    </div>
  );
}

function SourceLog({ snapshots, mobile, live }) {
  const latest = snapshots[snapshots.length - 1];
  const unavailable = latest?.source_log?.unavailable || [];
  const stubInputs = [...new Set(snapshots.flatMap((s) => (s.source_log?.entries || []).filter((e) => isStub(e.source || "")).map((e) => e.input || e.field || "—")))];
  return (
    <>
      {unavailable.length > 0 ? (
        <DataBanner kind="unavailable" title={`UNAVAILABLE · ${unavailable.length} ${unavailable.length === 1 ? "input" : "inputs"} at R${latest.run}`} data-testid="unavailable-panel">
          <span className="sn-stack" style={{ gap: "var(--space-2)" }}>
            {unavailable.map((u, i) => (
              <span key={i} className="sn-stack" style={{ gap: 2 }} data-testid={`unavailable-${i}`}>
                <span className="sn-num" style={{ color: "var(--text-primary)", fontWeight: 500 }}>{u.field}</span>
                <span className="sn-small">{u.reason}{u.fallback ? <span className="sn-faint"> · fallback: {u.fallback}</span> : null}</span>
              </span>
            ))}
          </span>
        </DataBanner>
      ) : (
        <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="unavailable-panel">No input was UNAVAILABLE at the latest snapshot.</p>
      )}

      {stubInputs.length > 0 && (
        <DataBanner kind="stub" title={`STUB · ${stubInputs.length} ${stubInputs.length === 1 ? "input" : "inputs"}`}>
          Synthetic, not market data: {stubInputs.join(", ")}.
        </DataBanner>
      )}

      {snapshots.map((s) => {
        const entries = s.source_log?.entries || [];
        return (
          <Panel key={s.id} flush data-testid={`snapshot-run-${s.run}`} icon="Lock"
            title={<>R{s.run}{s.run === 6 ? " · Post-close review" : s.payload?.stage ? ` · ${String(s.payload.stage).charAt(0)}${String(s.payload.stage).slice(1).toLowerCase()}` : ""}</>}
            meta={<><DataStateBadge state="FROZEN" /><span className="sn-num">cutoff {etAndLocal(s.cutoff_et, { day: selectedDate })} · snapshot {String(s.id).slice(0, 8)}</span></>}>
            {mobile ? (
              <div className="sn-cards" style={{ padding: "var(--space-3)" }}>{entries.map((e, i) => <SourceCard key={i} e={e} live={live} />)}</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <div role="table" aria-label={`Source log for R${s.run}`} style={{ minWidth: 760 }}>
                  <SourceHeader />
                  {entries.map((e, i) => <SourceRow key={i} e={e} live={live} />)}
                </div>
              </div>
            )}
          </Panel>
        );
      })}
    </>
  );
}
